'use client';

import { forwardRef, useImperativeHandle } from 'react';
import { useRenderPanelLogic } from '../../hooks/use-render-panel-logic';
import { useRenderStore } from '../../model/render-form.store';
import { InlineContainer } from './containers/InlineContainer';
import { RenderPanelContent } from './RenderPanelContent';
import { AlbumTabContent } from './AlbumTabContent';
import { EstimateSettingsPanel } from '@/features/design/estimate/ui/EstimateSettingsPanel';
import { useOptionalDesktopLayout } from '@/features/project/context/DesktopLayoutContext';
import { useProject } from '@/features/project/hooks/use-projects';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import type { Project } from '@/types/project';
import type { Concept, ConceptAngle, Angle, Control } from '@/types/concept';
import type { Render } from '@/types/render';

export interface UnifiedRenderPanelRef {
  submitRender: () => void;
}

interface UnifiedRenderPanelProps {
  // Core data
  project: Project;
  concept: Concept;
  conceptAngles: ConceptAngle[];
  angles: Record<string, Angle>;
  renders: Record<string, Render[]>;
  controls: Record<string, Control[]>;
  angleId?: string;
  currentRenderId?: string;

  // UI control
  showAlbumTab?: boolean;
  /** Stacked (single-column) layout: settings flow in the page scroll and the
   *  Generate CTA floats `fixed` above the bottom nav. When false the panel is
   *  the two-column inspector aside. Same component, different placement. */
  isStacked?: boolean;
  /** Tailwind `bottom-*` class for the stacked Generate capsule — clears the
   *  floating bottom-nav island when it's visible. */
  footerBottomClassName?: string;

  // Callbacks
  onRenderCreated?: (render: Render) => void;
  onRefetch?: () => void;
  isArchived?: boolean;
}

export const UnifiedRenderPanel = forwardRef<
  UnifiedRenderPanelRef,
  UnifiedRenderPanelProps
>(
  (
    {
      project,
      concept,
      conceptAngles,
      angles,
      renders,
      controls,
      angleId: propAngleId,
      currentRenderId,
      showAlbumTab,
      isStacked = false,
      footerBottomClassName,
      onRenderCreated,
      onRefetch,
      isArchived = false,
    },
    ref
  ) => {
    const layoutContext = useOptionalDesktopLayout();

    // Prefer the context-driven selected angle (two-column inspector + stacked
    // both read the same layout context), fall back to the explicit prop.
    const angleId = layoutContext?.selectedAngleId ?? propAngleId;

    // Get the currently selected render ID from concept editor store.
    // This reactively updates when the user switches renders in the carousel,
    // ensuring the settings panel always shows settings for the displayed render.
    const storeSelectedRenderId = useConceptStore((state) =>
      angleId ? state.ui.selectedRenderId[angleId] : undefined
    );
    // Prefer the reactive store value; fall back to the explicit prop.
    const activeRenderId = storeSelectedRenderId ?? currentRenderId;

    const shouldShowAlbumTab = showAlbumTab ?? true;

    // Use shared hook for all business logic
    const {
      mainAngle,
      currentAngleControls,
      angle,
      defaultValues,
      angleTabs,
      isRefineInProgress,
      refineParentRenderUrl,
      refineParentRenderId,
      isCreating,
      error,
      hasAnyChanges,
      handleCreateRender,
      resetChangesToDefaults,
      renderSettingsRef,
    } = useRenderPanelLogic({
      project,
      concept,
      conceptAngles,
      angles,
      renders,
      controls,
      angleId,
      selectedRenderId: activeRenderId,
      onRenderCreated,
      onRefetch,
    });

    const formData = useRenderStore((state) => state.formData);

    // The conceptAngle currently being edited — a derivative passes its own id,
    // the main panel falls back to the concept's main angle (both arrangements
    // omit `selectedAngleId` for main). Used both as the target angle id and to
    // tell a derivative apart from the main angle.
    const editingConceptAngleId = angleId ?? mainAngle?.id;

    // Derivative panels pass an explicit `angleId` (the conceptAngle id);
    // for the main angle the panel is opened with no `selectedAngleId` and
    // falls back to `mainAngle.id`. So a derivative is whenever the editing
    // conceptAngle id is anything other than the main angle's id.
    const isDerivative =
      !!editingConceptAngleId &&
      !!mainAngle?.id &&
      editingConceptAngleId !== mainAngle.id;

    const mode = formData?.mode ?? 'explore';

    // Explore main-angle requires filled project goals before Generate —
    // mirrors the brief gate (PromptsSection `goalsRequired`) + the
    // ProjectGoalsSection red dot. Refine reuses the parent goals; derivatives
    // have no goals row. Read live so filling goals in the embedded editor
    // re-enables Generate without reopening. Fed to the footer in
    // RenderPanelContent.
    const { data: projectResponse } = useProject(project.id);
    const hasProjectGoals =
      (projectResponse?.data?.attributes?.goals ?? '').trim().length > 0;
    const goalsRequired =
      !isDerivative && mode === 'explore' && !hasProjectGoals;

    // Expose submit function through ref
    useImperativeHandle(
      ref,
      () => ({
        submitRender: () => {
          if (renderSettingsRef.current?.submitRender) {
            renderSettingsRef.current.submitRender();
          }
        },
      }),
      [renderSettingsRef]
    );

    const settingsContent = (
      <RenderPanelContent
        projectId={project.id}
        concept={concept}
        conceptAngleId={editingConceptAngleId}
        angle={angle}
        viewpointLabel={
          conceptAngles.find((ca) => ca.id === editingConceptAngleId)
            ?.relationships?.angle?.meta?.viewpoint_label ?? null
        }
        controls={currentAngleControls}
        defaultValues={defaultValues}
        variant={angleId ? 'derivative' : 'main'}
        isCreating={isCreating}
        error={error}
        hasAnyChanges={hasAnyChanges}
        onSubmit={handleCreateRender}
        onResetChanges={resetChangesToDefaults}
        onRefetch={onRefetch}
        renderSettingsRef={renderSettingsRef}
        goalsRequired={goalsRequired}
        isReadOnly={isRefineInProgress}
        readOnlyParentRenderUrl={refineParentRenderUrl}
        readOnlyParentRenderId={refineParentRenderId}
        isStacked={isStacked}
        footerBottomClassName={footerBottomClassName}
      />
    );

    const albumContent = shouldShowAlbumTab ? (
      <AlbumTabContent
        conceptId={concept.id}
        projectId={project.id}
        conceptAngles={conceptAngles}
        isArchived={isArchived}
      />
    ) : null;

    // Estimate-tab inspector tools (settings + Regenerate) — the estimate
    // document itself renders in the content column (ConceptEditor).
    const estimateContent = (
      <EstimateSettingsPanel projectId={project.id} conceptId={concept.id} />
    );

    return (
      <InlineContainer
        albumContent={albumContent}
        estimateContent={estimateContent}
        angleTabs={angleTabs}
        selectedAngleId={angleId}
        onAngleChange={(id) => layoutContext?.setSelectedAngleId(id)}
        mainAngleId={mainAngle?.id}
        isStacked={isStacked}
      >
        {settingsContent}
      </InlineContainer>
    );
  }
);

UnifiedRenderPanel.displayName = 'UnifiedRenderPanel';
